/**
 * DepthLogix service catalog.
 *
 * GENERATED from the four existing service data files:
 *   src/pages/Services/DigitalTransformation/data_DigTrans.jsx
 *   src/pages/Services/EmergingTech/data_EmergTech.jsx
 *   src/pages/Services/BussinessApp/data_BussApp.jsx
 *   src/pages/Services/DigitalMarketing/data_DigiM.jsx
 *
 * Every title, description, feature, technology, duration, price and URL
 * below is copied verbatim from those files. Nothing here is invented.
 *
 * It exists so listing pages (the services index, the homepage services
 * section, the footer, the navigation) can be driven from one place
 * without importing the heavy detail-page data — and so that the
 * 25 existing service URLs can never drift out of sync.
 *
 * If you change a service in a data_*.jsx file, re-run the generator or
 * mirror the change here.
 */

export const SERVICE_CATEGORIES = [
  {
    key: "digital-transformation",
    label: "Digital Transformation",
    blurb: "Web platforms, mobile apps, custom software and SaaS products built from scratch.",
    basePath: "/services/DigitalTransformation",
  },
  {
    key: "emerging-tech",
    label: "Emerging Technologies",
    blurb: "AI systems, agents and data pipelines integrated into real business workflows.",
    basePath: "/services/EmergingTech",
  },
  {
    key: "business-apps",
    label: "Business Applications",
    blurb: "ERP, CRM and internal platforms on Dynamics 365, Power Platform, Salesforce and Odoo.",
    basePath: "/services/BusinessApp",
  },
  {
    key: "digital-marketing",
    label: "Digital Marketing",
    blurb: "Growth, commerce and lifecycle work that runs on top of what we build.",
    basePath: "/services/DigitalMarketing",
  },
];

export const SERVICES = [
  // Digital Transformation
  {
    categoryKey: "digital-transformation",
    category: "Digital Transformation",
    slug: "web-development",
    path: "/services/DigitalTransformation/web-development",
    title: "Web Development",
    description: "Building scalable web applications with modern technologies and frameworks.",
    duration: "4-12 weeks",
    price: "Starting at $5,000",
    features: ["Custom Web Applications", "React & Next.js Development", "Responsive Design", "API Integration", "Database Design", "Performance Optimization", "SEO Optimization", "Security Implementation"],
    technologies: ["React", "Next.js", "Vue.js", "Node.js", "TypeScript", "PostgreSQL", "MongoDB", "GraphQL"],
  },
  {
    categoryKey: "digital-transformation",
    category: "Digital Transformation",
    slug: "app-development",
    path: "/services/DigitalTransformation/app-development",
    title: "Mobile App Development",
    description: "iOS & Android solutions with React Native and native technologies.",
    duration: "6-16 weeks",
    price: "Starting at $8,000",
    features: ["Cross-Platform Development", "iOS & Android Compatibility", "Native Performance", "Push Notifications", "Offline Capability", "App Store Deployment", "API Integration", "Real-time Features"],
    technologies: ["React Native", "Flutter", "iOS Swift", "Android Kotlin", "Firebase", "Redux"],
  },
  {
    categoryKey: "digital-transformation",
    category: "Digital Transformation",
    slug: "custom-software",
    path: "/services/DigitalTransformation/custom-software",
    title: "Custom Software",
    description: "Tailored software solutions for your unique business needs.",
    duration: "8-24 weeks",
    price: "Starting at $15,000",
    features: ["Business Process Automation", "Custom Workflow Solutions", "Legacy System Modernization", "Integration Services", "Scalable Architecture", "Enterprise-grade Security", "Custom Reporting", "Maintenance & Support"],
    technologies: [".NET Core", "Java Spring", "Python Django", "Docker", "Kubernetes", "AWS", "Azure"],
  },
  {
    categoryKey: "digital-transformation",
    category: "Digital Transformation",
    slug: "ui-ux-design",
    path: "/services/DigitalTransformation/ui-ux-design",
    title: "UI/UX Design",
    description: "Modern, intuitive interfaces that enhance user experience and drive engagement.",
    duration: "2-8 weeks",
    price: "Starting at $3,000",
    features: ["User Research & Analysis", "Wireframing & Prototyping", "Visual Design", "Interaction Design", "Design Systems", "Usability Testing", "Responsive Design", "Design-to-Development Handoff"],
    technologies: ["Figma", "Adobe XD", "Sketch", "InVision", "Principle", "Webflow"],
  },
  {
    categoryKey: "digital-transformation",
    category: "Digital Transformation",
    slug: "SaaS",
    path: "/services/DigitalTransformation/SaaS",
    title: "SaaS Development",
    description: "Cloud-based software solutions with subscription models and scalable architecture.",
    duration: "12-36 weeks",
    price: "Starting at $25,000",
    features: ["Multi-tenant Architecture", "Subscription Management", "Payment Integration", "Scalable Infrastructure", "API Development", "Analytics Dashboard", "User Management", "Automated Billing"],
    technologies: ["React", "Node.js", "PostgreSQL", "Stripe", "AWS", "Docker", "Redis"],
  },
  // Emerging Technologies
  {
    categoryKey: "emerging-tech",
    category: "Emerging Technologies",
    slug: "enterprise-ai-solutions",
    path: "/services/EmergingTech/enterprise-ai-solutions",
    title: "Enterprise AI Solutions",
    description: "Custom AI systems tailored for large-scale business operations and decision making.",
    duration: "8-20 weeks",
    price: "Starting at $20,000",
    features: ["AI Strategy Consulting", "Machine Learning Models", "Predictive Analytics", "Process Automation", "Data Pipeline Development", "Model Training & Deployment", "Performance Monitoring", "Integration with Existing Systems"],
    technologies: ["Python", "TensorFlow", "PyTorch", "AWS SageMaker", "Azure ML", "Google AI"],
  },
  {
    categoryKey: "emerging-tech",
    category: "Emerging Technologies",
    slug: "ai-agents-ai-chatbots",
    path: "/services/EmergingTech/ai-agents-ai-chatbots",
    title: "AI Agents & Chatbots",
    description: "Intelligent conversational AI for customer service and business automation.",
    duration: "4-12 weeks",
    price: "Starting at $8,000",
    features: ["Natural Language Processing", "Multi-channel Deployment", "Sentiment Analysis", "Conversation Flow Design", "Integration with CRM", "24/7 Customer Support", "Analytics & Reporting", "Continuous Learning"],
    technologies: ["Dialogflow", "Azure Bot Service", "Rasa", "OpenAI", "WhatsApp API", "Telegram API"],
  },
  {
    categoryKey: "emerging-tech",
    category: "Emerging Technologies",
    slug: "generative-ai",
    path: "/services/EmergingTech/generative-ai",
    title: "Generative AI",
    description: "Creative AI solutions for content generation, design, and innovation.",
    duration: "6-16 weeks",
    price: "Starting at $12,000",
    features: ["Content Generation", "Image & Video Creation", "Code Generation", "Text-to-Speech", "Creative Design Assistance", "Personalized Content", "API Integration", "Custom Model Training"],
    technologies: ["OpenAI GPT", "DALL-E", "Stable Diffusion", "Midjourney API", "Custom Models", "LangChain"],
  },
  {
    categoryKey: "emerging-tech",
    category: "Emerging Technologies",
    slug: "ai-systems-integration",
    path: "/services/EmergingTech/ai-systems-integration",
    title: "AI Systems Integration",
    description: "Seamlessly integrate AI capabilities into your existing business systems and workflows.",
    duration: "6-18 weeks",
    price: "Starting at $15,000",
    features: ["Legacy System Integration", "API Development & Integration", "Data Pipeline Setup", "Real-time Data Processing", "System Architecture Design", "Security Implementation", "Performance Optimization", "Ongoing Maintenance"],
    technologies: ["Python", "REST APIs", "GraphQL", "Apache Kafka", "Docker", "Kubernetes", "AWS", "Azure"],
  },
  {
    categoryKey: "emerging-tech",
    category: "Emerging Technologies",
    slug: "data-analytics",
    path: "/services/EmergingTech/data-analytics",
    title: "Data Analytics",
    description: "Transform raw data into actionable insights for informed business decisions.",
    duration: "4-12 weeks",
    price: "Starting at $10,000",
    features: ["Business Intelligence Dashboards", "Data Visualization", "Predictive Analytics", "KPI Tracking", "Custom Reporting", "Data Warehousing", "Real-time Analytics", "Performance Metrics"],
    technologies: ["Tableau", "Power BI", "Python", "SQL", "Google Analytics", "Mixpanel", "Apache Spark"],
  },
  // Business Applications
  {
    categoryKey: "business-apps",
    category: "Business Applications",
    slug: "dynamics-365-erp",
    path: "/services/BusinessApp/dynamics-365-erp",
    title: "Dynamics 365 ERP",
    description: "End-to-end enterprise resource planning solutions that streamline your business operations, finance, and supply chain management.",
    duration: "6-12 weeks",
    price: null,
    features: ["Financial Management", "Supply Chain Automation", "Inventory Control", "Manufacturing Operations", "Project Management", "Business Intelligence", "Compliance Management", "Multi-currency Support"],
    technologies: ["Microsoft Dynamics 365", "Power BI", "Azure SQL", "Power Automate", "Common Data Service"],
  },
  {
    categoryKey: "business-apps",
    category: "Business Applications",
    slug: "dynamics-365-crm",
    path: "/services/BusinessApp/dynamics-365-crm",
    title: "Dynamics 365 CRM",
    description: "Comprehensive customer relationship management platform that enhances customer engagement and drives sales growth.",
    duration: "4-8 weeks",
    price: null,
    features: ["Sales Automation", "Marketing Automation", "Customer Service", "Lead Management", "Opportunity Tracking", "Customer Analytics", "Email Integration", "Mobile CRM"],
    technologies: ["Dynamics 365", "Power Platform", "Microsoft 365", "Azure", "Power Apps"],
  },
  {
    categoryKey: "business-apps",
    category: "Business Applications",
    slug: "power-apps",
    path: "/services/BusinessApp/power-apps",
    title: "Power Apps",
    description: "Build custom business applications rapidly with low-code platform to solve unique business challenges.",
    duration: "2-6 weeks",
    price: null,
    features: ["Canvas Apps", "Model-driven Apps", "Portal Development", "API Integration", "Mobile Optimization", "Workflow Automation", "Data Integration", "Custom Connectors"],
    technologies: ["Power Apps", "Power Automate", "Dataverse", "Power BI", "Azure Services"],
  },
  {
    categoryKey: "business-apps",
    category: "Business Applications",
    slug: "salesforce",
    path: "/services/BusinessApp/salesforce",
    title: "Salesforce Implementation",
    description: "Complete Salesforce CRM implementation and customization to transform your customer experience.",
    duration: "8-16 weeks",
    price: null,
    features: ["Sales Cloud", "Service Cloud", "Marketing Cloud", "Community Cloud", "Custom Objects", "Apex Development", "Lightning Components", "Integration Services"],
    technologies: ["Sales Cloud", "Service Cloud", "Apex", "Lightning", "Heroku", "MuleSoft"],
  },
  {
    categoryKey: "business-apps",
    category: "Business Applications",
    slug: "odoo",
    path: "/services/BusinessApp/odoo",
    title: "Odoo ERP",
    description: "Open-source ERP solutions that provide comprehensive business management capabilities at an affordable cost.",
    duration: "4-10 weeks",
    price: null,
    features: ["Accounting & Finance", "Inventory Management", "Sales & CRM", "Purchase Management", "Manufacturing", "Project Management", "Human Resources", "Website Builder"],
    technologies: ["Odoo", "Python", "PostgreSQL", "JavaScript", "XML-RPC"],
  },
  // Digital Marketing
  {
    categoryKey: "digital-marketing",
    category: "Digital Marketing",
    slug: "tiktok-shop-management",
    path: "/services/DigitalMarketing/tiktok-shop-management",
    title: "TikTok Shop Management",
    description: "Complete TikTok Shop setup, optimization, and management to skyrocket your sales through viral commerce.",
    duration: "2-4 weeks",
    price: null,
    features: ["Shop Setup & Verification", "Product Listing Optimization", "Content Strategy Development", "Live Shopping Events", "Inventory Management", "Analytics & Reporting", "Customer Service Setup", "Performance Optimization"],
    technologies: ["TikTok Shop", "TikTok API", "Analytics Tools", "CRM Integration", "Payment Gateways"],
  },
  {
    categoryKey: "digital-marketing",
    category: "Digital Marketing",
    slug: "tiktok-ads",
    path: "/services/DigitalMarketing/tiktok-ads",
    title: "TikTok Advertising",
    description: "Leverage TikTok's powerful algorithm to reach millions of potential customers with engaging video ads.",
    duration: "1-2 weeks",
    price: null,
    features: ["Creative Ad Development", "Audience Targeting", "Budget Optimization", "Performance Analytics", "A/B Testing", "Conversion Tracking", "Retargeting Campaigns", "ROI Maximization"],
    technologies: ["TikTok Ads Manager", "Pixel Integration", "Analytics", "Creative Suite", "Tracking Tools"],
  },
  {
    categoryKey: "digital-marketing",
    category: "Digital Marketing",
    slug: "meta-ads",
    path: "/services/DigitalMarketing/meta-ads",
    title: "Meta Advertising",
    description: "Strategic Facebook and Instagram advertising campaigns that drive real business results across the Meta ecosystem.",
    duration: "1-3 weeks",
    price: null,
    features: ["Facebook Ads Management", "Instagram Campaigns", "Audience Network", "Lead Generation", "Sales Conversion", "Brand Awareness", "Lookalike Audiences", "Cross-platform Strategy"],
    technologies: ["Meta Business Suite", "Facebook Pixel", "Instagram API", "Analytics", "CRM Integration"],
  },
  {
    categoryKey: "digital-marketing",
    category: "Digital Marketing",
    slug: "ppc-ads",
    path: "/services/DigitalMarketing/ppc-ads",
    title: "PPC Advertising",
    description: "Data-driven pay-per-click campaigns across Google, Bing, and social platforms that deliver measurable ROI.",
    duration: "1-2 weeks",
    price: null,
    features: ["Google Ads Management", "Bing Ads Optimization", "Keyword Strategy", "Landing Page Optimization", "Conversion Tracking", "Bid Management", "Competitor Analysis", "ROI Reporting"],
    technologies: ["Google Ads", "Bing Ads", "Google Analytics", "Tag Manager", "Optimization Tools"],
  },
  {
    categoryKey: "digital-marketing",
    category: "Digital Marketing",
    slug: "app-and-product-marketing",
    path: "/services/DigitalMarketing/app-and-product-marketing",
    title: "App & Product Marketing",
    description: "Comprehensive marketing strategies to launch and scale your mobile apps and digital products successfully.",
    duration: "4-8 weeks",
    price: null,
    features: ["App Store Optimization", "Launch Strategy", "User Acquisition", "Retention Campaigns", "Influencer Marketing", "PR & Outreach", "Analytics Setup", "Growth Hacking"],
    technologies: ["ASO Tools", "Analytics Platforms", "MMP Integration", "Social Media", "Email Marketing"],
  },
  {
    categoryKey: "digital-marketing",
    category: "Digital Marketing",
    slug: "email-marketing",
    path: "/services/DigitalMarketing/email-marketing",
    title: "Email Marketing",
    description: "Strategic email campaigns that nurture leads, drive conversions, and build lasting customer relationships.",
    duration: "2-4 weeks",
    price: null,
    features: ["Newsletter Campaigns", "Automation Workflows", "Lead Nurturing", "Segmentation Strategy", "A/B Testing", "Performance Analytics", "Template Design", "Deliverability Optimization"],
    technologies: ["Mailchimp", "Klaviyo", "HubSpot", "ActiveCampaign", "ConvertKit"],
  },
  {
    categoryKey: "digital-marketing",
    category: "Digital Marketing",
    slug: "seo",
    path: "/services/DigitalMarketing/seo",
    title: "Search Engine Optimization",
    description: "Comprehensive SEO strategies that improve your website's visibility and drive organic traffic from search engines.",
    duration: "3-6 months",
    price: null,
    features: ["Technical SEO Audit", "Keyword Research", "On-page Optimization", "Content Strategy", "Link Building", "Local SEO", "Performance Monitoring", "Competitor Analysis"],
    technologies: ["Google Search Console", "SEMrush", "Ahrefs", "Google Analytics", "Screaming Frog"],
  },
  {
    categoryKey: "digital-marketing",
    category: "Digital Marketing",
    slug: "design-&-development",
    path: "/services/DigitalMarketing/design-&-development",
    title: "Design & Development",
    description: "Stunning website design and development that converts visitors into customers with seamless user experiences.",
    duration: "4-12 weeks",
    price: null,
    features: ["UI/UX Design", "Responsive Development", "E-commerce Solutions", "Custom Web Applications", "Performance Optimization", "Security Implementation", "CMS Integration", "Mobile Optimization"],
    technologies: ["React", "Next.js", "WordPress", "Shopify", "Webflow", "Figma"],
  },
  {
    categoryKey: "digital-marketing",
    category: "Digital Marketing",
    slug: "maintenance-&-support",
    path: "/services/DigitalMarketing/maintenance-&-support",
    title: "Maintenance & Support",
    description: "Ongoing website maintenance and technical support to ensure your digital presence remains secure and high-performing.",
    duration: "Ongoing",
    price: null,
    features: ["Security Updates", "Performance Monitoring", "Backup Management", "Content Updates", "Bug Fixes", "Technical Support", "Uptime Monitoring", "Plugin/Theme Updates"],
    technologies: ["Monitoring Tools", "Backup Systems", "Security Scanners", "CDN", "SSL Certificates"],
  },
  {
    categoryKey: "digital-marketing",
    category: "Digital Marketing",
    slug: "automation-&-apps",
    path: "/services/DigitalMarketing/automation-&-apps",
    title: "Automation & Apps",
    description: "Custom automation solutions and application development that streamline your business processes and boost efficiency.",
    duration: "4-16 weeks",
    price: null,
    features: ["Workflow Automation", "Custom App Development", "API Integration", "Process Optimization", "Data Automation", "Mobile App Development", "CRM Integration", "Reporting Automation"],
    technologies: ["Python", "Node.js", "Zapier", "Make.com", "React Native", "REST APIs"],
  },
];

/** Services belonging to one category, in source order. */
export function servicesByCategory(categoryKey) {
  return SERVICES.filter((service) => service.categoryKey === categoryKey);
}

/** Look up one service by its category key and slug. */
export function findService(categoryKey, slug) {
  return SERVICES.find(
    (service) => service.categoryKey === categoryKey && service.slug === slug
  );
}

/**
 * Every distinct technology named across the catalog, with the number of
 * services that list it. Sorted by frequency, then alphabetically.
 */
export function technologyIndex() {
  const counts = new Map();
  for (const service of SERVICES) {
    for (const tech of service.technologies) {
      counts.set(tech, (counts.get(tech) || 0) + 1);
    }
  }
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}
